import useJwt from "@src/auth/jwt/useJwt";
import axios from "axios";
import themeConfig from "@configs/themeConfig";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { handleLogout } from "@store/actions/auth";

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0;

// ** Returns K format from a number
export const kFormatter = (num) =>
  num > 999 ? `${(num / 1000).toFixed(1)}k` : num;

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  );
};

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = { month: "short", day: "numeric", year: "numeric" }
) => {
  if (!value) return value;
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value);
  let formatting = { month: "short", day: "numeric" };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" };
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export function getCookies() {
  return document.cookie.split(";").reduce((cookies, cookie) => {
    const [name, ...rest] = cookie.trim().split("=");
    cookies[name] = rest.join("=");
    return cookies;
  }, {});
}
export function removeCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
}
export function getParsedCookie(name) {
  const raw = getCookies()[name];
  if (!raw) return null;

  try {
    const decoded = decodeURIComponent(raw); // فك الترميز
    const parsed = JSON.parse(decoded); // تحويل إلى كائن
    return parsed;
  } catch (error) {
    console.error(`Error parsing cookie "${name}":`, error.message);
    return null;
  }
}

export const isUserLoggedIn = () => getParsedCookie("sso-session-token");
export const getUserData = () => getParsedCookie("user-profile-membership");

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === "admin") return "/";
  if (userRole === "client") return "/access-control";
  return "/login";
};

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#7367f01a", // for option hover bg-color
    primary: "#7367f0", // for selected option bg-color
    neutral10: "#7367f0", // for tags bg-color
    neutral20: "#ededed", // for input border-color
    neutral30: "#ededed", // for input hover border-color
  },
});
export const config = useJwt.jwtConfig;

export const token = getParsedCookie("sso-session-token")?.token;

export const getAxiosInstance = () => {
  const token = getParsedCookie("sso-session-token")?.token;
  return axios.create({
    baseURL: themeConfig.app.api_url + "/",
    headers: { Authorization: `Bearer ${token}` },
  });
};

export function handleError404(error, history, msg, url) {
  if (error.response && error.response.status === 404) {
    history.push(`/not-found?msg=${msg}&backurl=${url}`);
  }
}

export function generateOptions(data, name = "name", id = "id") {
  let options = data.map((item) => {
    return (
      <option value={item[id]} key={item[id]}>
        {item[name]}
      </option>
    );
  });
  return options;
}

export function handleError401(error, history) {
  if (error.response && error.response.status === 403) {
    history.replace("/not-authorized");
    return;
  }
  if (error.response && error.response.status === 401) {
    const token = String(localStorage.getItem(config.storageTokenKeyName));
    axios
      .post(`${themeConfig.app.api_url}/logout`, null, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {});
    localStorage.removeItem("userData");
    removeCookie("sso-session-token");
    localStorage.removeItem(config.storageTokenKeyName);
    localStorage.removeItem(config.storageRefreshTokenKeyName);
    localStorage.removeItem("skin");
    history.push("/login");
  }
}

export function handleErrorAlert422(error, key) {
  if (error.response && error.response.status === 422) {
    if (error.response.data.errors[key].length) {
      const MySwal = withReactContent(Swal);
      return MySwal.fire({
        title: "Error",
        text: error.response.data.errors[key][0],
        icon: "error",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    }
  }
}

export function displayServer422Errors(error, setError) {
  if (error.response && error.response.status === 422) {
    let errors = error.response.data.errors;
    for (let key in errors) {
      setError(key, { type: "manual", message: errors[key][0] });
    }
  }
}
